import { AfterViewInit, Component, computed, effect, inject } from '@angular/core';
import { CustomWidget } from '../widget';
import * as maplibregl from 'maplibre-gl';
import { environment } from '@env';
import { WidgetType } from '@constants/widgets';
import { MapService } from '@services/ui/maps.service';

@Component({
  selector: 'abb-map-widget',
  standalone: true,
  imports: [],
  template: `<div
    [id]="htmlId()"
    class="overflow-hidden flex-grow"
    style="height: 100%;"
  ></div> `,
  styles: ``,
})
export class MapWidgetComponent extends CustomWidget implements AfterViewInit {
  mapService = inject(MapService);
  map: maplibregl.Map | undefined;
  private markers: { [id: string]: maplibregl.Marker } = {};
  private popups: { [id: string]: maplibregl.Popup } = {};
  htmlId = computed(() => 'map-widget-' + this.widget().id);

  coordinates = computed(() => {
    const data = this.data();
    if (data && data.__typename === 'MapData') {
      return data.coordinates;
    }
    return [];
  });

  constructor() {
    super();
    effect(() => {
      const coordinates = this.coordinates();
      if (this.map && coordinates.length > 0) {
        this.addMarkers();
      }
    });
  }

  private loadMap(): void {
    if (!this.map) {
      this.map = new maplibregl.Map({
        container: this.htmlId(),
        style: `https://maps.geo.${environment.maps.region}.amazonaws.com/maps/v0/maps/${environment.maps.mapName}/style-descriptor?key=${environment.maps.apiKey}`,
        center: [-98.5795, 39.8283],
        zoom: 5,
        preserveDrawingBuffer: true, // preserve the drawing buffer
      });

      this.map?.on('load', () => {
        if (this.map) {
          this.mapService.registerMap(this.htmlId(), this.map);
        }
        this.map?.addControl(
          new maplibregl.NavigationControl(),
          'bottom-right'
        );
        this.addMarkers();
      });
    } else {
      this.addMarkers();
    }
  }

  private createCircleMarker(): HTMLElement {
    const marker = document.createElement('div');
    marker.className = 'custom-marker';
    marker.style.width = '18px';
    marker.style.height = '18px';
    marker.style.borderRadius = '50%';
    marker.style.backgroundColor = 'rgb(255 0 15)';
    marker.style.border = '2px solid white';
    marker.style.boxShadow = '2px 2px 10px rgba(0,0,0,0.3)';
    return marker;
  }

  private addMarkers(): void {

    if (!this.map) return;

    Object.values(this.markers).forEach((marker) => marker.remove());
    Object.values(this.popups).forEach((popup) => popup.remove());
    this.markers = {};
    this.popups = {};

    this.coordinates().forEach((coordinate) => {
      const popup = new maplibregl.Popup({
        offset: 25,
        closeButton: false,
      }).setHTML(
        this.widget().type === WidgetType.LOCATION
          ? `<a href="/properties/${coordinate.id}/events"><b>${coordinate.label}</b></a>`
          : `<b>${coordinate.label}</b>`
      );

      popup?.on('open', () => {
        this.map?.flyTo({
          center: [coordinate.lng, coordinate.lat],
          zoom: 5,
          speed: 1.5,
        });
      });

      const marker = new maplibregl.Marker({
        element: this.createCircleMarker(),
      })
        .setLngLat([coordinate.lng, coordinate.lat])
        .setPopup(popup);
      if (this.map) {
        marker.addTo(this.map);
      }

      this.markers[coordinate.id] = marker;
      this.popups[coordinate.id] = popup;
    });

    if (Object.keys(this.markers).length > 1) {
      const bounds = new maplibregl.LngLatBounds();
      Object.values(this.markers).forEach((marker) =>
        bounds.extend(marker.getLngLat())
      );
      this.map?.fitBounds(bounds, { padding: 50 });
    } else {
      //se ho solo una properità la mappa si centra su questa con uno zoom "più ad alto livello"
      const firstCoordinate = this.coordinates()[0]; // Prendo la prima coordinata
      this.map!.flyTo({
        center: [firstCoordinate.lng, firstCoordinate.lat], // Uso la posizione del primo marker
        zoom: 11,
        speed: 1.5,
      });
    }
  }

  ngAfterViewInit(): void {
    requestAnimationFrame(() => {
      this.loadMap();
    });
  }
}
